import { csvCell } from './reportingContract.mjs'

export const TIMEZONE = 'Europe/London'
export const ALL_DATES = { period_start: '0001-01-01', period_end_exclusive: '9999-12-31', reporting_timezone: TIMEZONE }
export const terminal = row => ['cancelled', 'voided', 'declined'].includes(row.status)
export const verified = row => row.balance_confidence === 'confirmed' && Number.isSafeInteger(row.remaining_minor) && row.remaining_minor >= 0
export const words = value => String(value || 'Unknown').replace(/_/g, ' ')
export function money(value, currency = 'gbp') {
  if (!Number.isSafeInteger(value) || !/^[a-z]{3}$/i.test(currency || '')) return 'Unknown'
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency.toUpperCase() }).format(value / 100)
}
export function dayAt(value = new Date(), timezone = TIMEZONE) {
  if (value == null || value === "") return null
  const d = new Date(value)
  if (Number.isNaN(d.valueOf())) return null
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d)
  return ['year', 'month', 'day'].map(k => parts.find(p => p.type === k).value).join('-')
}
export function addDays(day, count) {
  const d = new Date(`${day}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + count)
  return d.toISOString().slice(0, 10)
}
export function periodPreset(key, today = dayAt()) {
  const date = new Date(`${today}T12:00:00Z`)
  const monday = addDays(today, -((date.getUTCDay() + 6) % 7))
  if (key === 'next') return { from: addDays(monday, 7), to: addDays(monday, 13) }
  if (key === 'month') return { from: `${today.slice(0, 7)}-01`, to: new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0, 12)).toISOString().slice(0, 10) }
  return { from: monday, to: addDays(monday, 6) }
}
export const inRange = (day, range) => Boolean(day && range.from <= day && day <= range.to)
export function dateText(day) {
  return day ? new Date(`${day.slice(0, 10)}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : 'Not set'
}
export const timestampText = value => value ? new Date(value).toLocaleString('en-GB', { timeZone: TIMEZONE, day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' }) : 'Not yet updated'

// Resolve a wall-clock time without relying on the browser's timezone or guessing a DST fold.
export function localTimeChoices(value, timezone = TIMEZONE) {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return []
  const naive = Date.parse(`${value}:00Z`)
  if (!Number.isFinite(naive)) return []
  const formatter = new Intl.DateTimeFormat('en-GB', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
  const choices = []
  for (let offset = -14 * 60; offset <= 14 * 60; offset += 15) {
    const instant = new Date(naive - offset * 60000)
    const p = Object.fromEntries(formatter.formatToParts(instant).map(x => [x.type, x.value]))
    if (`${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}` === value) choices.push(instant.toISOString())
  }
  return [...new Set(choices)].sort()
}
export function localToInstant(value, choice = '') {
  const choices = localTimeChoices(value)
  if (!choices.length) throw new Error('This local time does not exist. Choose a valid workshop time, allowing for the clock change.')
  if (choices.length > 1 && !choices.includes(choice)) throw new Error('This time occurs twice when the clocks change. Choose the first or second occurrence.')
  return choices.length === 1 ? choices[0] : choice
}
export const issueLabels = { payment: 'Check recorded payment', balance: 'Check remaining payment', date: 'Add expected payment date', schedule: 'Confirm scheduling', stock: 'Review unresolved stock charge', tax: 'Confirm VAT treatment' }
export function bookedValue(row) {
  const supported = Number.isSafeInteger(row.canonical_gross_minor) && (row.entity_type === 'booking' ? row.confirmed_price_minor != null : row.canonical_price_resolved === true && row.balance_confidence !== 'provisional')
  return { ...row, agreed_value_minor: supported ? row.canonical_gross_minor : null, agreed_value_included: supported, agreed_value_basis: supported ? 'Recorded agreed value; not a cash forecast' : 'Provisional or unresolved price excluded; recorded job total retained separately' }
}
export const settlementLabels = { cash_received_awaiting_banking: 'Cash · banking not confirmed', customer_paid_processor_settlement_pending: 'Processor settlement pending', customer_paid_settlement_unverified: 'Settlement evidence missing', partial_or_unresolved: 'Partial or unresolved bank evidence' }
export const settlementCheck = receipt => receipt.quality_codes?.includes('SETTLEMENT_PARTIAL_OR_UNRESOLVED') ? 'partial_or_unresolved' : receipt.settlement_state
export function settlementGroups(receipts) {
  return Object.entries(settlementLabels).map(([state, label]) => ({ state, label, rows: receipts.filter(r => settlementCheck(r) === state) }))
}
export function issuesFor(row) {
  const issues = []
  if (row.payment_status === 'paid_requires_reconciliation') issues.push('payment')
  else if (!terminal(row) && !verified(row)) issues.push('balance')
  if (!terminal(row) && row.remaining_minor !== 0 && !row.expected_payment_date) issues.push('date')
  if (!terminal(row) && !['completed', 'collected'].includes(row.status) && !row.scheduled_date) issues.push('schedule')
  if (row.quality_codes?.includes('UNRESOLVED_CHARGEABLE_STOCK')) issues.push('stock')
  if (row.vat_minor == null) issues.push('tax')
  return issues
}
export function paymentLabel(row) {
  if (terminal(row)) return 'Cancelled'
  if (!verified(row)) return row.payment_status === 'paid_requires_reconciliation' ? 'Recorded payment needs checking' : 'Balance needs checking'
  if (row.remaining_minor === 0) return row.payment_status === 'paid' ? 'Paid' : 'Nothing left to collect'
  return row.allocated_receipts_minor > 0 ? 'Part paid' : 'Awaiting payment'
}
export function totalMoney(rows, field) {
  const totals = {}
  let unknown = 0
  for (const r of rows) {
    if (!Number.isSafeInteger(r[field]) || !/^[a-z]{3}$/i.test(r.currency || '')) { unknown++; continue }
    totals[r.currency] = (totals[r.currency] || 0) + r[field]
    if (!Number.isSafeInteger(totals[r.currency])) throw new Error('Total exceeds the supported range.')
  }
  return { totals, unknown, text: Object.entries(totals).map(([c, n]) => money(n, c)).join(' · ') || (unknown ? 'Not yet available' : '£0.00') }
}
export function matchingRows(rows, search) {
  const q = search.trim().toLowerCase()
  return rows.filter(r => !q || [r.customer_display_name, r.job_number, r.invoice_number, r.service_summary, r.reference].some(v => String(v || '').toLowerCase().includes(q)))
}
export function reportRows(report, { view, range, status = 'open', search = '', issue = '', earlier = false, today = dayAt() }) {
  let rows = report?.jobs || []
  if (view === 'checks') rows = rows.filter(r => issue ? issuesFor(r).includes(issue) : issuesFor(r).some(k => k !== 'tax'))
  else {
    rows = rows.filter(r => status === 'cancelled' ? terminal(r) : status === 'all' || !terminal(r))
    if (status === 'open') rows = rows.filter(r => view === 'booked' ? !['completed', 'collected'].includes(r.status) : r.remaining_minor !== 0)
    rows = rows.filter(r => view === 'booked' ? inRange(r.scheduled_date, range) : verified(r) && (earlier ? r.remaining_minor > 0 && r.expected_payment_date && r.expected_payment_date < today : inRange(r.expected_payment_date, range)))
  }
  const dateField = view === 'booked' ? 'scheduled_date' : 'expected_payment_date'
  return matchingRows(rows, search).map(r => view === 'booked' ? bookedValue(r) : r)
    .sort((a, b) => (a[dateField] || '9999-12-31').localeCompare(b[dateField] || '9999-12-31') || String(a.job_number || '').localeCompare(String(b.job_number || ''), 'en', { numeric: true }) || a.entity_id.localeCompare(b.entity_id))
}
export function summaryRows(report, key, today = dayAt()) {
  const rows = report?.jobs || []
  if (key === 'checks') return rows.filter(r => issuesFor(r).some(k => k !== 'tax'))
  return rows.filter(r => !terminal(r) && verified(r) && r.remaining_minor > 0 && inRange(r.expected_payment_date, periodPreset(key, today)))
}
export function chartGroups(rows, { dateField, measure, unit = 'day', currency = 'gbp', method = false }) {
  const groups = new Map()
  let excluded = 0
  for (const row of rows) {
    const day = row[dateField]
    const value = measure === 'count' ? 1 : row[measure]
    if ((!method && !day) || !Number.isFinite(value) || (measure !== 'count' && measure !== 'planned_minutes' && row.currency !== currency)) { excluded++; continue }
    const key = method ? methodLabel(row.payment_method) : unit === 'week' ? periodPreset('week', day).from : day
    if (!groups.has(key)) groups.set(key, { key, label: method ? key : dateText(key), value: 0, ids: [] })
    const group = groups.get(key)
    group.value += measure === 'planned_minutes' ? value / 60 : measure === 'count' ? value : value / 100
    group.ids.push(row.entity_id || row.receipt_id || row.id)
  }
  return { groups: [...groups.values()].sort((a, b) => a.key.localeCompare(b.key)), excluded }
}
export const methodLabel = method => ({ cash: 'Cash', bank_transfer: 'Bank transfer', card: 'Card terminal', stripe: 'Stripe', other: 'Other', manual: 'Unknown method' }[method] || 'Unknown method')
export function viewCsv(rows, metadata) {
  const fields = [...new Set(rows.flatMap(Object.keys))]
  const meta = Object.entries(metadata)
  const head = [...meta.map(([k]) => `report_${k}`), ...fields].map(csvCell).join(',')
  const body = rows.map(r => [...meta.map(([, v]) => v), ...fields.map(k => r[k])].map(csvCell).join(','))
  return '\uFEFF' + [head, ...body].join('\r\n') + '\r\n'
}
export function downloadCsv(rows, metadata, name) {
  const url = URL.createObjectURL(new Blob([viewCsv(rows, metadata)], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = `${name}.csv`
  a.click()
  URL.revokeObjectURL(url)
}
